import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { StatusPill } from '@/components/px/StatusPill'
import { Button } from '@/components/ui/button'
import { navGroups, pxNav, type NavItem, type NavItemState, type NavModuleKey } from '@/components/px/nav'
import { navText } from '@/components/px/navText'
import { navGroupsForRole } from '@/access/roleAccess'
import { roleDefinitionFor } from '@/access/roles'
import { useAppSelector } from '@/store'
import { selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'

/**
 * Home — deck screen 0.1, rebuilt as the module overview (TASK-33343000,
 * Agon's ruling 2026-09-19). It reads the registry and the role policy only:
 * no import from `@/demo`, no request, no figure, count or date. One card per
 * module the role can reach (plus Administration), each listing that
 * module's deck screens with a Built / Coming soon chip and an Open action to
 * the module's first BUILT screen — `destinationFor` guarantees that
 * destination is `link`-stated (spec §6.6).
 */
export interface HomeScreenChip {
  /** The deck reference, e.g. "1.3" — empty for a row with no deck screen. */
  deckRef: string
  label: string
  state: NavItemState
}

export interface HomeModuleCard {
  key: NavModuleKey
  title: string
  blurb: string
  screens: readonly HomeScreenChip[]
  openLabel: string
  openTo: string
}

/**
 * The deck reference for a rail item's destination — display data for this
 * page only, kept out of `nav.ts` (the navigation MODEL declares structure
 * and derived state, not which mockup slide a row illustrates). Keyed by the
 * literal `to` the model builds when `navGroups(null)` has no concrete
 * engagement — the placeholder project id `'{id}'` never resolves to a real
 * URL, so it is safe as a display-only lookup key here.
 */
const DECK_REF_BY_PATH: Readonly<Record<string, string>> = {
  '/architecture': '0.2',
  '/resources/pool': '1.1',
  '/resources/capacity': '1.3',
  '/resources/allocations': '1.4',
  '/resources/skills': '1.5',
  '/resources/utilisation': '1.6',
  '/resources/pipeline': '1.7',
  '/resources/training': '1.8',
  '/resources/badges': '1.9',
  '/projects': '2.1',
  '/projects/health': '2.2',
  '/library/templates': '2.3',
  '/library/requirements': '2.5',
  '/library/agents': '2.6',
  '/library/blueprints': '2.9',
  '/library/playbooks': '2.10',
  '/clients': '2.11',
  '/project': '3.1',
  '/project/{id}/interviews': '3.2',
  '/project/{id}/requirements': '3.3',
  '/project/{id}/blueprint': '3.4',
  '/project/{id}/build': '3.5',
  '/project/{id}/migration': '3.6',
  '/project/{id}/test': '3.7',
  '/project/{id}/go-live': '3.8',
  '/project/{id}/ams': '3.9',
}

/** Compares the EN `label` (the item's identity), never the rendered text. */
const deckRefFor = (item: NavItem): string => {
  if (item.to === undefined) return item.label === 'Roles & permissions' ? '0.3' : ''
  return DECK_REF_BY_PATH[item.to] ?? ''
}

export default function HomePage() {
  const role = useAppSelector(selectActiveRoleKey)
  const { t } = useTranslation(['common', 'nav'])
  const groups = navGroupsForRole(role, navGroups(null))
  const blurbByKey = new Map(
    navGroups(null)
      .flatMap((group) => group.modules)
      .map((module) => [module.key, module.blurb] as const)
  )

  const cards: readonly HomeModuleCard[] = groups
    .filter((group) => group.key === 'modules' || group.key === 'admin')
    .flatMap((group) => group.destinations)
    .map((destination) => {
      const title = navText(t, destination.labelKey, destination.label)
      return {
        key: destination.key,
        title,
        blurb: blurbByKey.get(destination.key) ?? '',
        screens: destination.sections.flatMap((section) =>
          section.items.map((item) => ({
            deckRef: deckRefFor(item),
            label: navText(t, item.labelKey, item.label),
            state: item.state,
          }))
        ),
        openLabel: t('home.openModule', { module: title }),
        openTo: destination.to,
      }
    })

  return (
    <AppShell
      moduleLabel="PX"
      breadcrumb={navText(t, 'item.home', 'Home')}
      contextPill={t(roleDefinitionFor(role).labelKey).toUpperCase()}
      nav={pxNav(role)}
    >
      {/* `pb-24` keeps the last card row clear of the floating feedback pill
          (bottom-right, `index.html`) when scrolled to the end (finding 7). */}
      <div className="w-full pb-24">
        <header className="max-w-2xl">
          <h1 className="font-serif text-2xl font-semibold text-foreground">{t('home.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('home.lede')}</p>
        </header>

        {/* Owner ruling (2026-09-19): two cards per row instead of four, so
            each card gets more room — also resolves the deferred low about
            screen labels clipping at 1280px. */}
        <section
          className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2"
          aria-label={t('home.modulesAriaLabel')}
        >
          {cards.map((card) => (
            <HomeModuleCardView key={card.key} card={card} />
          ))}
        </section>
      </div>
    </AppShell>
  )
}

function HomeModuleCardView({ card }: { card: HomeModuleCard }) {
  const { t } = useTranslation('common')
  return (
    <article className="flex flex-col rounded-lg border border-border bg-card px-4 py-4">
      <h2 className="font-serif text-lg font-semibold leading-tight text-foreground">{card.title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t(card.blurb)}</p>
      <ul className="mt-3 flex-1 space-y-1.5">
        {card.screens.map((screen, index) => (
          <li key={`${screen.label}-${index}`} className="flex items-start justify-between gap-2">
            <span className="flex min-w-0 items-start gap-1.5 text-sm text-foreground">
              {screen.deckRef !== '' && (
                // A structural mockup reference (e.g. "1.3"), not a KPI figure —
                // deliberately NOT the `v-figure` convention class (AC-5: no
                // figure, count or date rendered on this page).
                <span className="shrink-0 font-mono text-[0.6875rem] text-muted-foreground" aria-hidden>
                  {screen.deckRef}
                </span>
              )}
              {/* Owner ruling (2026-09-19): the wider two-per-row card gives
                  this label room — it wraps instead of truncating (resolves
                  the deferred low VIS-C-08 about labels clipping at 1280px). */}
              <span>{screen.label}</span>
            </span>
            <StatusPill tone={screen.state === 'link' ? 'done' : 'neutral'}>
              {screen.state === 'link' ? t('home.built') : t('nav.comingSoon')}
            </StatusPill>
          </li>
        ))}
      </ul>
      <div className="mt-4">
        <Button asChild size="sm" variant="accent">
          <Link to={card.openTo}>
            {card.openLabel}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </Button>
      </div>
    </article>
  )
}
